<template>
 <div id="panel2" ref="rootPanel">
   <slot :appProp="app"></slot>
 </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeMount, onBeforeUnmount, toRaw, provide } from 'vue'
import { Application, Graphics, Text, TextStyle } from 'pixi.js-legacy'

import bootstrapCss from 'bootstrap/dist/css/bootstrap.min.css?raw'
// Props
const props = defineProps({
  height: Number,
  width: Number,
  range: Array,
  labels: Array,
  issueName: String,
  showLabel: Boolean
})

// Refs
let app = null
const rootPanel = ref(null)
let bgInstance = null //new Graphics()
const frameInstance = new Graphics()
let textLabels = []
let latestTap = 0

// Computed
const fromUnit = computed(() => Number(props.range[0]))
const toUnit = computed(() => Number(props.range[1]))
const pixelsPerUnit = computed(() => (props.width - 70) / (toUnit.value - fromUnit.value))

// Provide values
provide('convertToPixels', convertToPixels)
provide('convertToUnits', convertToUnits)
provide('panelHeight', props.height)
provide('panelWidth', props.width)
provide('addToPanel', addToPanel)
provide('pixiApp', app)
provide('textLabels', textLabels)

// Methods
function convertToPixels(units) {
  return ((parseFloat(units) - fromUnit.value) * pixelsPerUnit.value) + 35
}

function convertToUnits(pixels) {
  return ((pixels - 35) / pixelsPerUnit.value) + fromUnit.value
}

function simpleClick() {
  let now = new Date().getTime()
  let timeSince = now - latestTap
  latestTap = now

  if (timeSince < 600) {
    emit('double-tap')
    latestTap = 0
  }
}

function clicked(event) {
  emit('click-proposal', event.global.x)
}

function addToPanel(child) {
  // console.log("panel - addToPanel");
  //console.log(app);
  app.stage.addChild(toRaw(child))
  //console.log("end addToPanel");
}

function render() {
  bgInstance.name = 'Grid Background'
  bgInstance.beginFill(0xEEEEEE)
  bgInstance.drawRect(35, 10, props.width - 70, props.height * 2 / 3)
  bgInstance.endFill()
  bgInstance.zIndex = 0
  bgInstance.eventMode = 'static'
  bgInstance.on('pointerdown', simpleClick)
  addToPanel(bgInstance)

  frameInstance.name = 'Horizontal Line'
  frameInstance.beginFill(0x000000)
  frameInstance.drawRect(35, 10 + props.height / 3, props.width - 70, 1)

  frameInstance.drawRect(35, 10, 1, props.height * 2 / 3)
  frameInstance.drawRect(props.width - 35, 10, 1, props.height * 2 / 3)
  frameInstance.endFill()
  frameInstance.zIndex = 5
  addToPanel(frameInstance)

  bgInstance.on('pointerdown', clicked)
}

function renderLabels() {
  let gridLabelStyle = new TextStyle({ fontSize: 15, stroke: '#000000' })
  let boldStyle = new TextStyle({ fontSize: 15, stroke: '#000000', fontWeight: 'bold' })
  let bigBoldStyle = new TextStyle({ fontSize: 17, stroke: '#000000', fontWeight: 'bold' })

  for (let label of props.labels) {
    let style = props.range.includes(label) ? boldStyle : gridLabelStyle
    let labelText = new Text(label, style)
    labelText.x = convertToPixels(label)
    labelText.y = 10 + props.height / 30 * 22
    labelText.anchor.set(0.5)
    labelText.zIndex = 5
    addToPanel(labelText)
    textLabels.push(labelText)
  }

  let labelText = new Text(props.issueName, bigBoldStyle)
  labelText.x = props.width / 2 - labelText.width / 2
  labelText.y = 20 + props.height / 30 * 24
  labelText.zIndex = 5
  if (!props.showLabel) {
    addToPanel(labelText)
    textLabels.push(labelText)
  }
}

function removeLabels() {
  while (textLabels.length > 0) {
    let labelText = textLabels.pop()
    labelText.parent.removeChild(labelText)
    labelText.destroy({ children: true })
  }
}

function reRenderLabels() {
  removeLabels()
  renderLabels()
}

function reRenderChildren(children) {
  if (!children) return

  for (let child of children) {
    if (child.reRender) child.reRender()
    if (child.$children && child.$children.length > 0) {
      reRenderChildren(child.$children)
    }
  }
}

// Emit (manual binding required for setup)
const emit = defineEmits(['double-tap', 'click-proposal'])

// Watchers
watch(() => props.range, (newVal, oldVal) => {
  if (newVal[0] === oldVal[0] && newVal[1] === oldVal[1]) return
  reRenderChildren() // Note: Need to pass children explicitly if needed
  reRenderLabels()
})

watch(() => props.labels, () => {
  reRenderLabels()
})

watch(() => props.issueName, () => {
  reRenderLabels()
})

// Lifecycle
onBeforeUnmount(() => {
  bgInstance.off('pointerdown', simpleClick)
  bgInstance.off('pointerdown', clicked)
  app.destroy(true)
})
onBeforeMount(() => {
  app = new Application({
    name: 'PIXI Application',
    width: props.width,
    height: props.height,
    transparent: false,
    backgroundColor: 0xFFFFFF,
    antialias: true,
    resolution: window.devicePixelRatio,
    autoDensity: true
  })
  bgInstance = new Graphics()
  app.stage.sortableChildren = true
  render()
  renderLabels()
})
onMounted(() => {
  const el = rootPanel.value
  if (!el) return

  const root = el.getRootNode()

  // If inside shadow DOM, this will be a ShadowRoot
  if (root instanceof ShadowRoot) {
    app.view.classList.add("p-2")
    const targetChild = root.querySelector('#app-root'); 
    const canvasRoot = document.createElement('div')
    canvasRoot.id = 'canvas-root'
    canvasRoot.classList.add("justify-content-center");
    canvasRoot.classList.add("d-flex");
    canvasRoot.appendChild(app.view)

    targetChild.appendChild(canvasRoot)
  } else {
    // fallback if not in shadow DOM
    document.body.appendChild(app.view)
  }
})
</script>
